import { Box } from '../Box/Box'
import { Button } from '../Button/Button'
import { ComponentProps, FC, useRef } from 'react'
import { TextField, TextFieldProps } from '../TextField/TextField'
import { useFileUpload } from './FileUpload.hooks'
import Upload from '@mui/icons-material/Upload'

export type ProcessedFileFormats = File | Blob | string

export enum FileUploadReturnType {
  FILE = 'file',
  OBJECT_URL = 'object_url',
  BASE64 = 'base64'
}

export type FileUploadPassthroughProps = {
  onChange: (x: ProcessedFileFormats) => void
  maxFileSizeMB?: number
  accept?: string
  returnType?: FileUploadReturnType
  onError?: () => void
}

export type FileUploadProps = Omit<TextFieldProps, 'adornment' | 'onChange'> &
  FileUploadPassthroughProps & {
    buttonProps?: Omit<ComponentProps<typeof Button>, 'onClick' | 'size'>
    isLoading?: boolean
  }

/**
 * FileUpload is a styled variant of MUI's TextField.
 *
 * File conversion logic should be stored here based on the FileUploadReturnType.
 * This prevents having many utils that all do the same conversions.
 *
 * The file or processed file is passed to the `onChange` handler.
 * The file processing method is determined by the `returnType`.
 * You can re-process the same file by changing the `returnType`.
 *
 * More info can be found at: https://mui.com/material-ui/react-text-field/
 */
export const FileUpload: FC<FileUploadProps> = ({
  accept,
  onChange,
  label,
  helperText,
  size,
  maxFileSizeMB = 10,
  returnType = FileUploadReturnType.FILE,
  buttonProps,
  disabled,
  isLoading = false,
  value,
  onError,
  ...rest
}: FileUploadProps) => {
  const hiddenRef = useRef<HTMLInputElement | null>(null)
  const keyRef = useRef(0)
  const { fileName, errorMessage, handleFileSelection } = useFileUpload({
    onChange: (...props) => {
      keyRef.current += 1
      onChange(...props)
    },
    accept,
    maxFileSizeMB,
    returnType,
    onError
  })

  const v = value && fileName ? fileName : ''

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center'
      }}
    >
      <TextField
        {...rest}
        startAdornment={<Upload />}
        label={label}
        size={size}
        value={v}
        disabled={disabled}
        error={Boolean(errorMessage.length)}
        helperText={errorMessage || helperText}
        endAdornment={
          <Button
            onClick={(): void => {
              if (hiddenRef.current) {
                hiddenRef.current.click()
              }
            }}
            size={size}
            disabled={disabled}
            loading={isLoading}
            {...buttonProps}
          >
            Upload
          </Button>
        }
      />
      <input
        key={keyRef.current}
        type="file"
        hidden
        accept={accept}
        ref={hiddenRef}
        onChange={handleFileSelection}
      />
    </Box>
  )
}
